// src/components/SetlistPlanner/SetlistPlanner.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
  pointerWithin,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import jsPDF from "jspdf";
import {
  Plus,
  Trash2,
  Download,
  Music,
  GripVertical,
  ListMusic,
  Edit3,
  Check,
  X,
} from "lucide-react";
import { useTheme } from "../../context/ThemeContext"; // ⬅️ sama teema kuin TietoaMeistä

/* ======================
   Aputyökalut
====================== */
const cx = (...c) => c.filter(Boolean).join(" ");
const uid =
  () =>
    (crypto?.randomUUID
      ? crypto.randomUUID()
      : `id_${Date.now()}_${Math.random().toString(36).slice(2)}`);

const LS_KEY = "setlist.planner.v1";
const TEXT = "font-customFont1 text-black/80 dark:text-white/80 dark:text-shadow-lg";

const parseDuration = (s) => {
  const str = String(s || "").trim();
  if (!str) return 0;
  if (/^\d+:\d{1,2}$/.test(str)) {
    const [m, sec] = str.split(":").map((n) => parseInt(n, 10) || 0);
    return Math.max(0, m) * 60 + Math.min(59, Math.max(0, sec));
  }
  if (/^\d+([.,]\d+)?$/.test(str)) {
    const mins = parseFloat(str.replace(",", ".")) || 0;
    return Math.round(mins * 60);
  }
  return 0;
};
const fmtMMSS = (secs) => {
  const s = Math.max(0, Math.round(secs || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
};
const sumSecs = (ids, songs) =>
  ids.reduce((acc, id) => acc + (songs[id]?.durationSec || 0), 0);

/* === Droppable-wrapper === */
function DroppableContainer({ id, className, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={cx(className, isOver && "ring-2 ring-primary/40 rounded-md")}
    >
      {children}
    </div>
  );
}

/* =========
   Sortable Song Row (+ muokkaus varastossa)
=========== */
function SortableSong({ id, song, onRemove, editable = false, onChange }) {
  const [editing, setEditing] = useState(false);
  const [tTitle, setTTitle] = useState(song.title || "");
  const [tArtist, setTArtist] = useState(song.artist || "");
  const [tDur, setTDur] = useState(song.durationSec ? fmtMMSS(song.durationSec) : "");
  const [tBpm, setTBpm] = useState(song.bpm ? String(song.bpm) : "");
  const [tKey, setTKey] = useState(song.key || "");

  useEffect(() => {
    if (editing) {
      setTTitle(song.title || "");
      setTArtist(song.artist || "");
      setTDur(song.durationSec ? fmtMMSS(song.durationSec) : "");
      setTBpm(song.bpm ? String(song.bpm) : "");
      setTKey(song.key || "");
    }
  }, [editing]); // eslint-disable-line

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id, disabled: editing });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const saveEdit = () => {
    if (!onChange) return setEditing(false);
    const next = {
      ...song,
      title: tTitle.trim(),
      artist: tArtist.trim(),
      durationSec: parseDuration(tDur),
      bpm: tBpm.trim() ? parseInt(tBpm, 10) : null,
      key: tKey.trim().toUpperCase(),
    };
    onChange(id, next);
    setEditing(false);
  };

  if (editing) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={cx(
          "flex flex-col gap-2 rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-black p-3",
          TEXT
        )}
      >
        <div className="grid gap-2 md:grid-cols-5">
          <input
            className={cx(
              "rounded-md border border-black/10 dark:border-white/10 bg-white dark:bg-black px-2 py-1 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm",
              TEXT,
              "placeholder-black/50 dark:placeholder-white/50"
            )}
            placeholder="Nimi"
            value={tTitle}
            onChange={(e) => setTTitle(e.target.value)}
          />
          <input
            className={cx(
              "rounded-md border border-black/10 dark:border-white/10 bg-white dark:bg-black px-2 py-1 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm",
              TEXT,
              "placeholder-black/50 dark:placeholder-white/50"
            )}
            placeholder="Artisti"
            value={tArtist}
            onChange={(e) => setTArtist(e.target.value)}
          />
          <input
            className={cx(
              "rounded-md border border-black/10 dark:border-white/10 bg-white dark:bg-black px-2 py-1 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm",
              TEXT,
              "placeholder-black/50 dark:placeholder-white/50"
            )}
            placeholder="Kesto (mm:ss tai min, esim. 3:30 tai 3.5)"
            value={tDur}
            onChange={(e) => setTDur(e.target.value)}
          />
          <input
            className={cx(
              "rounded-md border border-black/10 dark:border-white/10 bg-white dark:bg-black px-2 py-1 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm",
              TEXT,
              "placeholder-black/50 dark:placeholder-white/50"
            )}
            placeholder="BPM"
            inputMode="numeric"
            value={tBpm}
            onChange={(e) => setTBpm(e.target.value.replace(/[^\d]/g, ""))}
          />
          <input
            className={cx(
              "rounded-md border border-black/10 dark:border-white/10 bg-white dark:bg-black px-2 py-1 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm",
              TEXT,
              "placeholder-black/50 dark:placeholder-white/50"
            )}
            placeholder="Sävellaji (esim. Am, G)"
            value={tKey}
            onChange={(e) => setTKey(e.target.value)}
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={saveEdit}
            className={cx(
              "inline-flex items-center gap-1 rounded-md border border-black/10 px-2 py-1 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm hover:bg-black/5 dark:border-white/10 dark:hover:bg-white/10",
              TEXT
            )}
            title="Tallenna"
          >
            <Check className="h-4 w-4" /> Tallenna
          </button>
          <button
            onClick={() => setEditing(false)}
            className={cx(
              "inline-flex items-center gap-1 rounded-md border border-black/10 px-2 py-1 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm hover:bg-black/5 dark:border-white/10 dark:hover:bg-white/10",
              TEXT
            )}
            title="Peruuta"
          >
            <X className="h-4 w-4" /> Peruuta
          </button>
          {onRemove && (
            <button
              onClick={onRemove}
              className={cx(
                "ml-auto inline-flex items-center gap-1 rounded-md border border-black/10 px-2 py-1 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm hover:bg-black/5 dark:border-white/10 dark:hover:bg-white/10",
                TEXT
              )}
              title="Poista kappale"
            >
              <Trash2 className="h-4 w-4" /> Poista
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cx(
        "group flex items-center gap-3 rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-black px-3 py-2",
        isDragging && "opacity-70 ring-2 ring-primary"
      )}
    >
      <button
        className="cursor-grab active:cursor-grabbing opacity-60 hover:opacity-100"
        {...attributes}
        {...listeners}
        aria-label="Siirrä"
        title="Siirrä"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">
        <div className={cx("truncate font-medium", TEXT)}>
          {song.title}{" "}
          {song.artist ? <span className="opacity-70">– {song.artist}</span> : null}
        </div>
        <div className={cx("dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm opacity-80", TEXT)}>
          {fmtMMSS(song.durationSec)}
          {song.bpm ? ` · ${song.bpm} BPM` : ""}
          {song.key ? ` · ${song.key}` : ""}
        </div>
      </div>

      {editable && (
        <button
          onClick={() => setEditing(true)}
          className="rounded-md p-1 opacity-60 hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/10"
          aria-label="Muokkaa kappaletta"
          title="Muokkaa kappaletta"
        >
          <Edit3 className="h-4 w-4" />
        </button>
      )}

      {onRemove && (
        <button
          onClick={onRemove}
          className="rounded-md p-1 opacity-60 hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/10"
          aria-label="Poista kappale"
          title="Poista kappale"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

/* =========
   Droppable List Container
=========== */
function SongList({
  title,
  icon: Icon,
  items,
  songs,
  droppableId,
  onRemove,
  canEdit = false,
  onEdit,
  cardBg, // ⬅️ tuodaan ulkoa sama tausta
}) {
  return (
    <div className={cx("rounded-2xl backdrop-blur-md shadow-lg ring-1 p-4", cardBg)}>
      <div className="mb-3 flex items-center justify-between">
        <div className={cx("inline-flex items-center gap-2 font-semibold", TEXT)}>
          <Icon className="h-4 w-4" />
          <span>{title}</span>
        </div>
        <div className={cx("dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm opacity-80", TEXT)}>
          {fmtMMSS(sumSecs(items, songs))}
        </div>
      </div>

      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <DroppableContainer id={droppableId} className="space-y-2 min-h-[48px]">
          {items.length === 0 && (
            <div
              className={cx(
                "rounded-md border border-dashed border-black/20 dark:border-white/25 py-6 text-center dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm",
                TEXT,
                "opacity-70"
              )}
            >
              Raahaa kappaleita tähän
            </div>
          )}
          {items.map((id) => (
            <SortableSong
              key={id}
              id={id}
              song={songs[id]}
              editable={canEdit}
              onChange={onEdit}
              onRemove={onRemove ? () => onRemove(id) : undefined}
            />
          ))}
        </DroppableContainer>
      </SortableContext>
    </div>
  );
}

/* =========================
   PÄÄKOMPONENTTI
========================= */
export default function SetlistPlanner() {
  const { theme } = useTheme();                         // ⬅️ teema käyttöön
  const cardBg =
    theme === "dark" ? "bg-[#0b0b0f]/60 ring-white/10" : "bg-lemon/60 ring-white/40"; // ⬅️ sama kuin TietoaMeistä

  const [songs, setSongs] = useState({});
  const [libraryIds, setLibraryIds] = useState([]);
  const [sets, setSets] = useState([
    { id: uid(), name: "Setti 1", songIds: [] },
    { id: uid(), name: "Setti 2", songIds: [] },
  ]);

  // Uuden kappaleen lomake
  const [tTitle, setTTitle] = useState("");
  const [tArtist, setTArtist] = useState("");
  const [tDur, setTDur] = useState("");
  const [tBpm, setTBpm] = useState("");
  const [tKey, setTKey] = useState("");

  // DnD
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  /* ---- localStorage ---- */
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (data?.songs) setSongs(data.songs);
      if (Array.isArray(data?.libraryIds)) setLibraryIds(data.libraryIds);
      if (Array.isArray(data?.sets)) setSets(data.sets);
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify({ songs, libraryIds, sets }));
  }, [songs, libraryIds, sets]);

  /* ---- apurit ---- */
  const allContainers = useMemo(
    () => ["library", ...sets.map((s) => s.id)],
    [sets]
  );

  const findContainerOfItem = useCallback(
    (songId) => {
      if (libraryIds.includes(songId)) return "library";
      const s = sets.find((st) => st.songIds.includes(songId));
      return s?.id || null;
    },
    [libraryIds, sets]
  );

  const insertAt = (arr, index, item) => {
    const a = arr.slice();
    a.splice(index, 0, item);
    return a;
  };

  /* ---- drag handlers ---- */
  const onDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;
    const activeId = active.id;
    const overId = over.id;

    const from = findContainerOfItem(activeId);
    const to = allContainers.includes(overId) ? overId : findContainerOfItem(overId);

    if (!from || !to || from === to) return;

    // Poista lähteestä
    if (from === "library") {
      setLibraryIds((prev) => prev.filter((id) => id !== activeId));
    } else {
      setSets((prev) =>
        prev.map((st) =>
          st.id === from
            ? { ...st, songIds: st.songIds.filter((id) => id !== activeId) }
            : st
        )
      );
    }

    // Lisää kohteeseen indeksin mukaan
    const overIndex = (() => {
      if (allContainers.includes(overId)) return null; // pudotus tyhjään konttiin → loppuun
      const list =
        to === "library"
          ? libraryIds
          : sets.find((s) => s.id === to)?.songIds || [];
      return Math.max(0, list.indexOf(overId));
    })();

    if (to === "library") {
      setLibraryIds((prev) =>
        overIndex == null ? [...prev, activeId] : insertAt(prev, overIndex, activeId)
      );
    } else {
      setSets((prev) =>
        prev.map((st) =>
          st.id === to
            ? {
                ...st,
                songIds:
                  overIndex == null
                    ? [...st.songIds, activeId]
                    : insertAt(st.songIds, overIndex, activeId),
              }
            : st
        )
      );
    }
  };

const onDragEnd = (event) => {
  const { active, over } = event;
  if (!over) return;
  const activeId = active.id;
  const overId = over.id;

  const container = findContainerOfItem(activeId);
  const overContainer = allContainers.includes(overId)
    ? overId
    : findContainerOfItem(overId);

  if (!container || container !== overContainer) return;

  const list =
    container === "library"
      ? libraryIds
      : sets.find((s) => s.id === container)?.songIds || [];

  const oldIndex = list.indexOf(activeId);
  const newIndex = (() => {
    if (overId === container) return list.length;
    const arr =
      container === "library"
        ? libraryIds
        : sets.find((s) => s.id === container).songIds;
    return arr.indexOf(overId);
  })();

  if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

  if (container === "library") {
    setLibraryIds((prev) => arrayMove(prev, oldIndex, newIndex));
  } else {
    setSets((prev) =>
      prev.map((st) =>
        st.id === container
          ? { ...st, songIds: arrayMove(st.songIds, oldIndex, newIndex) }
          : st
      )
    );
  }
};


  /* ---- kappaleen lisäys & muokkaus ---- */
  const addSong = (e) => {
    e?.preventDefault?.();
    const title = tTitle.trim();
    if (!title) return;
    const id = uid();
    const durationSec = parseDuration(tDur);
    const bpm = tBpm.trim() ? parseInt(tBpm, 10) : null;
    const song = {
      id,
      title,
      artist: tArtist.trim(),
      durationSec,
      bpm,
      key: tKey.trim().toUpperCase(),
    };
    setSongs((prev) => ({ ...prev, [id]: song }));
    setLibraryIds((prev) => [...prev, id]);
    setTTitle("");
    setTArtist("");
    setTDur("");
    setTBpm("");
    setTKey("");
  };

  const editSongInLibrary = (songId, next) => {
    setSongs((prev) => ({ ...prev, [songId]: next }));
  };

  const removeFromList = (listId, songId) => {
    if (listId === "library") {
      setLibraryIds((prev) => prev.filter((id) => id !== songId));
    } else {
      setSets((prev) =>
        prev.map((st) =>
          st.id === listId
            ? { ...st, songIds: st.songIds.filter((id) => id !== songId) }
            : st
        )
      );
    }
  };

  const removeSongEverywhere = (songId) => {
    setLibraryIds((prev) => prev.filter((id) => id !== songId));
    setSets((prev) =>
      prev.map((st) => ({ ...st, songIds: st.songIds.filter((id) => id !== songId) }))
    );
    setSongs((prev) => {
      const cp = { ...prev };
      delete cp[songId];
      return cp;
    });
  };

  const addSet = () => {
    setSets((prev) => [
      ...prev,
      { id: uid(), name: `Setti ${prev.length + 1}`, songIds: [] },
    ]);
  };
  const renameSet = (id, name) => {
    setSets((prev) => prev.map((s) => (s.id === id ? { ...s, name } : s)));
  };
  const deleteSet = (id) => {
    const st = sets.find((s) => s.id === id);
    if (st) setLibraryIds((prev) => [...prev, ...st.songIds]);
    setSets((prev) => prev.filter((s) => s.id !== id));
  };

  /* ---- PDF (vektoriteksti) ---- */
  const totalSecs = useMemo(
    () => sets.reduce((acc, s) => acc + sumSecs(s.songIds, songs), 0),
    [sets, songs]
  );

  const handleExportPdf = () => {
    const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 40;
    const lineH = 18;

    let y = margin;

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(16);
    pdf.text("Settilista", margin, y);
    y += 10;

    sets.forEach((s) => {
      // Setin otsikko
      y += 18;
      if (y > pageH - margin) {
        pdf.addPage();
        y = margin + 18;
      }
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(13);
      pdf.text(`${s.name} (${fmtMMSS(sumSecs(s.songIds, songs))})`, margin, y);

      // Kappaleet
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(11);

      if (s.songIds.length === 0) {
        y += lineH;
        pdf.text("— Ei kappaleita —", margin, y);
      } else {
        s.songIds.forEach((id) => {
          const sg = songs[id] || {};
          const rightParts = [];
          if (sg.durationSec) rightParts.push(fmtMMSS(sg.durationSec));
          if (sg.bpm) rightParts.push(`${sg.bpm} BPM`);
          if (sg.key) rightParts.push(sg.key);
          const rightTxt = rightParts.join(" · ");

          const title = sg.title + (sg.artist ? ` – ${sg.artist}` : "");
          const maxLeft = pageW - margin * 2 - 140; // tilaa oikealle palstalle
          const lines = pdf.splitTextToSize(title, maxLeft);

          lines.forEach((ln, idx) => {
            y += lineH;
            if (y > pageH - margin) {
              pdf.addPage();
              y = margin + lineH;
            }
            pdf.text(ln, margin, y);
            if (idx === lines.length - 1 && rightTxt) {
              pdf.text(rightTxt, pageW - margin, y, { align: "right" });
            }
          });
        });
      }
    });

    // Footer
    y = pageH - margin / 2;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.text(`Kesto yhteensä: ${fmtMMSS(totalSecs)}`, margin, y);

    pdf.save("settilista.pdf");
  };

  return (
    <section className={cx("rounded-2xl backdrop-blur-md shadow-lg ring-1 p-6", cardBg, TEXT)}>
      <h2 className={cx("mb-2 font-customFont2 dark:text-white/90 text-black/90 text-lg xxxsm:text-sm xxsm:text-sm s24:text-sm xsm:text-sm sm:text-lg md:text-lg mdd:text-lg font-semibold", TEXT)}>Settilista-suunnittelija</h2>
      <p className={cx("mb-6 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm opacity-80", TEXT)}>
        Kirjoita kappaleet varastoon, vedä &amp; pudota ne setteihin ja vie siisti PDF.
        Keston voi syöttää muodossa <b>mm:ss</b> (esim. 3:30) tai <b>minuutteina</b> (esim. 3.5).
      </p>

      {/* Työkalurivi */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <button
          onClick={addSet}
          className={cx(
            "rounded-xl border border-black/10 px-3 py-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm hover:bg-black/5 dark:border-white/10 dark:hover:bg-white/10 inline-flex items-center gap-2",
            TEXT
          )}
        >
          <Plus className="h-4 w-4" /> Lisää setti
        </button>
        <button
          onClick={handleExportPdf}
          className={cx(
            "rounded-xl border border-black/10 px-3 py-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm hover:bg-black/5 dark:border-white/10 dark:hover:bg-white/10 inline-flex items-center gap-2",
            TEXT
          )}
        >
          <Download className="h-4 w-4" /> Vie PDF
        </button>
        <div className={cx("ml-auto dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm opacity-80", TEXT)}>
          Kesto yhteensä: <b>{fmtMMSS(totalSecs)}</b>
        </div>
      </div>

      {/* Uusi kappale -lomake */}
      <form
        onSubmit={addSong}
        className={cx("rounded-xl p-4 backdrop-blur-md shadow-lg ring-1 mb-6", cardBg)}
      >
        <div className={cx("mb-3 inline-flex items-center gap-2 font-medium", TEXT)}>
          <Music className="h-4 w-4" /> Lisää kappale varastoon
        </div>
        <div className="grid gap-3 md:grid-cols-5">
          <input
            className={cx(
              "rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-black px-3 py-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm",
              TEXT,
              "placeholder-black/50 dark:placeholder-white/50"
            )}
            placeholder="Nimi (pakollinen)"
            value={tTitle}
            onChange={(e) => setTTitle(e.target.value)}
            required
          />
          <input
            className={cx(
              "rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-black px-3 py-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm",
              TEXT,
              "placeholder-black/50 dark:placeholder-white/50"
            )}
            placeholder="Artisti"
            value={tArtist}
            onChange={(e) => setTArtist(e.target.value)}
          />
          <input
            className={cx(
              "rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-black px-3 py-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm",
              TEXT,
              "placeholder-black/50 dark:placeholder-white/50"
            )}
            placeholder="Kesto (mm:ss tai min, esim. 3:30 tai 3.5)"
            value={tDur}
            onChange={(e) => setTDur(e.target.value)}
          />
          <input
            className={cx(
              "rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-black px-3 py-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm",
              TEXT,
              "placeholder-black/50 dark:placeholder-white/50"
            )}
            placeholder="BPM"
            inputMode="numeric"
            value={tBpm}
            onChange={(e) => setTBpm(e.target.value.replace(/[^\d]/g, ""))}
          />
          <input
            className={cx(
              "rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-black px-3 py-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm",
              TEXT,
              "placeholder-black/50 dark:placeholder-white/50"
            )}
            placeholder="Sävellaji (esim. Am, G)"
            value={tKey}
            onChange={(e) => setTKey(e.target.value)}
          />
        </div>
        <div className="mt-3">
          <button
            type="submit"
            className={cx(
              "rounded-xl border border-black/10 px-3 py-2 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm hover:bg-black/5 dark:border-white/10 dark:hover:bg-white/10 inline-flex items-center gap-2",
              TEXT
            )}
          >
            <Plus className="h-4 w-4" /> Lisää kappale
          </button>
        </div>
      </form>

      {/* Drag & Drop alueet */}
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragOver={onDragOver}
        onDragEnd={onDragEnd}
      >
        <div className="grid gap-6 md:grid-cols-2">
          {/* Varasto (muokattava) */}
          <SongList
            title="Kappalevarasto"
            icon={ListMusic}
            items={libraryIds}
            songs={songs}
            droppableId="library"
            canEdit
            onEdit={editSongInLibrary}
            onRemove={(id) => removeSongEverywhere(id)}
            cardBg={cardBg} // ⬅️ sama tausta
          />

          {/* Setit */}
          <div className="space-y-6">
            {sets.map((s) => (
              <div
                key={s.id}
                className={cx("rounded-2xl backdrop-blur-md shadow-lg ring-1 p-4", cardBg)}
              >
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div className="inline-flex items-center gap-2">
                    <Edit3 className="h-4 w-4 opacity-70" />
                    <input
                      value={s.name}
                      onChange={(e) => renameSet(s.id, e.target.value)}
                      className={cx(
                        "bg-transparent outline-none font-semibold border-b border-transparent focus:border-black/30 dark:focus:border-white/30",
                        TEXT,
                        "placeholder-black/50 dark:placeholder-white/50"
                      )}
                    />
                  </div>
                  <div className={cx("inline-flex items-center gap-3 dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm opacity-80", TEXT)}>
                    <span>{fmtMMSS(sumSecs(s.songIds, songs))}</span>
                    <button
                      onClick={() => deleteSet(s.id)}
                      className="rounded-md px-2 py-1 hover:bg-black/5 dark:hover:bg-white/10"
                      aria-label="Poista setti"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <SortableContext items={s.songIds} strategy={verticalListSortingStrategy}>
                  <DroppableContainer id={s.id} className="space-y-2 min-h-[48px]">
                    {s.songIds.length === 0 && (
                      <div
                        className={cx(
                          "rounded-md border border-dashed border-black/20 dark:border-white/25 py-6 text-center dark:text-white/80 text-black/80 font-customFont1 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm",
                          TEXT,
                          "opacity-70"
                        )}
                      >
                        Raahaa kappaleita tähän
                      </div>
                    )}
                    {s.songIds.map((id) => (
                      <SortableSong
                        key={id}
                        id={id}
                        song={songs[id]}
                        onRemove={() => removeFromList(s.id, id)}
                      />
                    ))}
                  </DroppableContainer>
                </SortableContext>
              </div>
            ))}
          </div>
        </div>

        <DragOverlay />
      </DndContext>
    </section>
  );
}
